// src/pages/Admin/ProjectsDashboard.js
import { useEffect, useMemo, useState } from "react";
import { adminLogout } from "../../services/adminAuth"; // ✅ ADDED
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  serverTimestamp,
  Timestamp,
  addDoc,
  deleteDoc,
  where,
} from "firebase/firestore";
import { ref as storageRef, listAll, deleteObject } from "firebase/storage";
import { db, storage } from "../../firebaseConfig";
import AdminGate from "../../components/AdminGate";

/* ============================== Helpers ============================== */
const clsx = (...xs) => xs.filter(Boolean).join(" ");
const clamp = (v) => Math.max(0, Math.min(100, Number(v || 0)));
const nowLocalISO = () => { const d = new Date(); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 16); };
function formatDate(ts) {
  try {
    if (!ts) return "—";

    let d = null;

    if (typeof ts.toDate === "function") d = ts.toDate();
    else if (ts instanceof Date) d = ts;
    else if (typeof ts === "number") d = new Date(ts > 1e12 ? ts : ts * 1000);
    else if (typeof ts === "string") d = new Date(ts);

    if (!d || isNaN(d.getTime())) return "—";

    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();

    let hh = d.getHours();
    const min = String(d.getMinutes()).padStart(2, "0");
    const ampm = hh >= 12 ? "PM" : "AM";
    hh = hh % 12 || 12;

    return `${dd}/${mm}/${yyyy}, ${String(hh).padStart(2, "0")}:${min} ${ampm}`;
  } catch {
    return "—";
  }
}

function formatDateOnly(ts) {
  try {
    if (!ts) return "—";

    let d = null;

    if (typeof ts.toDate === "function") d = ts.toDate();
    else if (ts instanceof Date) d = ts;
    else if (typeof ts === "number") d = new Date(ts > 1e12 ? ts : ts * 1000);
    else if (typeof ts === "string") d = new Date(ts);

    if (!d || isNaN(d.getTime())) return "—";

    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();

    return `${dd}/${mm}/${yyyy}`;
  } catch {
    return "—";
  }
}
function downloadCSV(filename, rows) {
  if (!rows?.length) return;
  const seen = new Set(Object.keys(rows[0])); rows.forEach(r => Object.keys(r).forEach(k => seen.add(k)));
  const headers = Array.from(seen);
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""').replace(/\r?\n/g, " ")}"`;
  const csv = [headers.join(","), ...rows.map(r => headers.map(h => esc(r[h])).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob); const a = document.createElement("a");
  a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
}

/* ======================== Stage Catalogue =========================== */
const PRE_WORK = [
  { code: "MOU", name: "MOU" },
  { code: "SURVEY", name: "Site Survey" },
  { code: "SOIL", name: "Soil Testing" },
  { code: "DESIGN", name: "Design & Structure" },
  { code: "TENDER", name: "Tender Process" },
  { code: "WORK", name: "Work Started" },
];
const POST_WORK = [
  { code: "EXCAVATION", name: "Excavation / Site Work" },
  { code: "FOOTING", name: "Footing" },
  { code: "PLINTH", name: "Plinth Beam" },
  { code: "COLUMN_BEAM", name: "Column & Beam Work" },
  { code: "BRICK", name: "Brick Masonry Work" },
  { code: "PLASTER", name: "Plaster Work" },
  { code: "PEB_INSPECTION", name: "PEB Inspection" },
  { code: "PEB_INSTALL", name: "PEB Installation" },
  { code: "COLOUR", name: "Colour Work" },
  { code: "LOGO", name: "Logo Design & Branding" },
  { code: "FINISH", name: "Finishing" },
  { code: "DONE", name: "Completed" },
];
const STAGE_ORDER = [...PRE_WORK, ...POST_WORK].map(x => x.code);
const NAME_BY = Object.fromEntries([...PRE_WORK, ...POST_WORK].map(x => [x.code, x.name]));
function mergeStages(existing = []) {
  const by = Object.fromEntries(existing.map(s => [s.code, s]));
  return STAGE_ORDER.map(code => by[code] ? { name: NAME_BY[code], ...by[code] } : { code, name: NAME_BY[code], status: "Pending" });
}
function computeProgress(stages = []) {
  const total = stages.length || 1;
  const done = stages.filter(s => String(s.status || "").toLowerCase() === "completed").length;
  return Math.round((done / total) * 100);
}
function computeCurrentStage(stages = []) {
  const nxt = stages.find(s => String(s.status || "").toLowerCase() !== "completed");
  return nxt ? nxt.name || nxt.code : "Completed";
}

/* ============================ Workflow / Stakeholder Sync Helpers ============================ */
function stageToWorkflowStatus(code = "", status = "") {
  const c = String(code || "").toUpperCase();
  const s = String(status || "").toLowerCase();

  if (c === "MOU") {
    if (s === "completed") return "MOU_COMPLETED";
    if (s === "in progress") return "MOU_IN_PROGRESS";
    if (s === "rejected") return "MOU_REJECTED";
    return "NEW";
  }

  const prefixByStage = {
    SURVEY: "SURVEY",
    SOIL: "SOIL",
    DESIGN: "DESIGN",
    TENDER: "TENDER",
    WORK: "WORK",
  };

  const prefix = prefixByStage[c];
  if (!prefix) return "";

  if (s === "completed") return `${prefix}_DONE`;
  if (s === "in progress") return `${prefix}_IN_PROGRESS`;
  if (s === "rejected") return `${prefix}_REJECTED`;
  return `${prefix}_PENDING`;
}

function stageToConstructionKey(code = "") {
  const map = {
    EXCAVATION: "EXCAVATION",
    FOOTING: "FOOTING",
    PLINTH: "PLINTH_BEAM",
    COLUMN_BEAM: "COLUMNS_BEAMS",
    BRICK: "BRICK_MASONRY",
    PLASTER: "PLASTER",
    PEB_INSPECTION: "PEB_INSPECTION",
    PEB_INSTALL: "PEB_INSTALLATION",
    COLOUR: "COLOUR_WORK",
    LOGO: "LOGO_BRANDING",
    FINISH: "FINISHING",
    DONE: "COMPLETED",
  };
  return map[String(code || "").toUpperCase()] || "";
}

function normalizeConstructionStatus(status = "") {
  const s = String(status || "").toLowerCase();
  if (s === "completed") return "COMPLETED";
  if (s === "in progress") return "IN_PROGRESS";
  if (s === "rejected") return "REJECTED";
  return "PENDING";
}

function getStageTimestamp(stage = {}) {
  return (
    stage.completedAt ||
    stage.startedAt ||
    stage.rejectedAt ||
    stage.lastStageUpdatedAt ||
    Timestamp.now()
  );
}

function computeConstructionOverallProgress(stages = []) {
  const postStages = (stages || []).filter((s) =>
    POST_WORK.some((p) => p.code === s.code)
  );
  const total = postStages.length || 1;
  const done = postStages.filter(
    (s) => String(s.status || "").toLowerCase() === "completed"
  ).length;
  return Math.round((done / total) * 100);
}

async function upsertGodownProjectByWorkflowId(workflowProjectId, project = {}, payload = {}) {
  if (!workflowProjectId) return;

  const qSnap = await getDocs(
    query(collection(db, "godown_projects"), where("workflowProjectId", "==", workflowProjectId))
  );

  const base = {
    workflowProjectId,
    pacsName: project.pacsName || project.name || "",
    projectTitle: project.workflowProjectTitle || project.name || project.pacsName || "",
    district: project.district || "",
    taluka: project.taluka || "",
    locationVillage: project.locationVillage || "",
    state: project.state || "Gujarat",
    stakeholderVisible: true,
    updatedAt: serverTimestamp(),
    projectDashboardSyncedAt: serverTimestamp(),
  };

  if (qSnap.empty) {
    await addDoc(collection(db, "godown_projects"), {
      ...base,
      ...payload,
      createdAt: serverTimestamp(),
    });
    return;
  }

  for (const d of qSnap.docs) {
    await updateDoc(doc(db, "godown_projects", d.id), {
      ...base,
      ...payload,
    });
  }
}

async function syncProjectStageToWorkflowAndStakeholder(project = {}, stages = [], changedCode = "") {
  try {
    const workflowProjectId = project.workflowProjectId;
    if (!workflowProjectId) return;

    const changedStage = (stages || []).find((s) => s.code === changedCode);
    if (!changedStage) return;

    const workflowStatus = stageToWorkflowStatus(changedStage.code, changedStage.status);
    const constructionKey = stageToConstructionKey(changedStage.code);

    if (workflowStatus) {
      await updateDoc(doc(db, "wf_projects", workflowProjectId), {
        status: workflowStatus,
        lastProjectDashboardStageCode: changedStage.code,
        lastProjectDashboardStageName: changedStage.name || NAME_BY[changedStage.code] || "",
        lastProjectDashboardStageStatus: changedStage.status || "",
        lastProjectDashboardStageNote: changedStage.lastStageNote || "",
        projectDashboardProgressPercent: computeProgress(stages),
        updatedAt: serverTimestamp(),
        projectDashboardSyncedAt: serverTimestamp(),
      });
    }

    if (constructionKey) {
      const stagePayload = {
        status: normalizeConstructionStatus(changedStage.status),
        note: changedStage.lastStageNote || "",
        remark: changedStage.lastStageNote || "",
        sourceStageCode: changedStage.code,
        sourceStageName: changedStage.name || NAME_BY[changedStage.code] || "",
        updatedAt: getStageTimestamp(changedStage),
        updatedBySource: "PROJECTS_DASHBOARD",
      };

      await upsertGodownProjectByWorkflowId(workflowProjectId, project, {
        [`constructionTimeline.${constructionKey}`]: stagePayload,
        constructionOverallProgress: computeConstructionOverallProgress(stages),
        latestConstructionStageCode: changedStage.code,
        latestConstructionStageName: changedStage.name || NAME_BY[changedStage.code] || "",
        latestConstructionStageStatus: changedStage.status || "",
        latestConstructionStageNote: changedStage.lastStageNote || "",
      });
    }
  } catch (e) {
    console.error("[syncProjectStageToWorkflowAndStakeholder] error", e);
  }
}

async function syncBulkStagesToWorkflow(project = {}, stages = []) {
  try {
    const workflowProjectId = project.workflowProjectId;
    if (!workflowProjectId) return;

    await updateDoc(doc(db, "wf_projects", workflowProjectId), {
      projectDashboardProgressPercent: computeProgress(stages),
      projectDashboardCurrentStage: computeCurrentStage(stages),
      updatedAt: serverTimestamp(),
      projectDashboardSyncedAt: serverTimestamp(),
    });

    const constructionTimelinePatch = {};
    stages.forEach((s) => {
      const key = stageToConstructionKey(s.code);
      if (!key) return;
      constructionTimelinePatch[`constructionTimeline.${key}`] = {
        status: normalizeConstructionStatus(s.status),
        note: s.lastStageNote || "",
        remark: s.lastStageNote || "",
        sourceStageCode: s.code,
        sourceStageName: s.name || NAME_BY[s.code] || "",
        updatedAt: getStageTimestamp(s),
        updatedBySource: "PROJECTS_DASHBOARD",
      };
    });

    await upsertGodownProjectByWorkflowId(workflowProjectId, project, {
      ...constructionTimelinePatch,
      constructionOverallProgress: computeConstructionOverallProgress(stages),
    });
  } catch (e) {
    console.error("[syncBulkStagesToWorkflow] error", e);
  }
}

async function syncContractorToStakeholder(project = {}, contractorPayload = {}) {
  try {
    const workflowProjectId = project.workflowProjectId;
    if (!workflowProjectId) return;

    await upsertGodownProjectByWorkflowId(workflowProjectId, project, {
      contractorName: contractorPayload.contractorName || "",
      contractorCompany: contractorPayload.contractorCompany || "",
      contractorMobile: contractorPayload.contractorMobile || "",
      contractorLoaDate: contractorPayload.loaDate || null,
      projectDashboardContractorSyncedAt: serverTimestamp(),
    });
  } catch (e) {
    console.error("[syncContractorToStakeholder] error", e);
  }
}

/* ============================== UI Bits ============================= */
const StatusBadge = ({ s }) => {
  const v = String(s || "Pending").toLowerCase();
  const cls =
    v === "completed" ? "bg-emerald-100 text-emerald-700" :
    v === "in progress" ? "bg-amber-100 text-amber-700" :
    v === "rejected" ? "bg-rose-100 text-rose-700" :
    "bg-slate-100 text-slate-700";
  const label =
    v === "completed" ? "Completed" :
    v === "in progress" ? "In Progress" :
    v === "rejected" ? "Rejected" : "Pending";
  return <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${cls}`}>{label}</span>;
};
const ProgressBar = ({ value = 0 }) => (
  <div className="w-full h-2.5 bg-slate-200 rounded-full overflow-hidden">
    <div className="h-2.5 bg-gradient-to-r from-teal-500 via-cyan-500 to-blue-600 rounded-full transition-all"
         style={{ width: `${clamp(value)}%` }}/>
  </div>
);
const SectionHeading = ({ title, subtitle }) => (
  <div className="flex items-end justify-between mb-2 mt-6">
    <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
    <div className="text-xs text-slate-500">{subtitle}</div>
  </div>
);
const Input = ({ label, value, onChange, type = "text", placeholder }) => (
  <div>
    <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
    <input
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
    />
  </div>
);
const StageCard = ({ s, onEdit }) => {
  const v = String(s.status || "Pending").toLowerCase();
  const stamp =
    v === "completed"   ? s.completedAt || s.lastStageUpdatedAt || s.startedAt :
    v === "in progress" ? s.startedAt   || s.lastStageUpdatedAt :
                          s.lastStageUpdatedAt;
  return (
    <div className="flex items-center justify-between gap-4 p-4 bg-white rounded-xl border border-slate-200 shadow-sm">
      <div className="min-w-0">
        <div className="text-slate-900 font-semibold">{s.name}</div>
        <div className="text-xs text-slate-500 mt-0.5">{v === "pending" ? "Awaiting start" : formatDate(stamp)}</div>
        {s.lastStageNote ? <div className="text-xs text-slate-500 mt-1 line-clamp-2">Note: {s.lastStageNote}</div> : null}
      </div>
      <div className="flex items-center gap-3">
        <StatusBadge s={s.status}/>
        <button onClick={onEdit} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs hover:bg-blue-700">Edit</button>
      </div>
    </div>
  );
};

/* ============================ PDF helpers ============================ */
async function fetchToDataURL(url) {
  try {
    const res = await fetch(url, { cache: "no-cache" });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(blob);
    });
  } catch { return null; }
}
async function renderLetterhead(pdf) {
  const W = pdf.internal.pageSize.getWidth();
  const H = pdf.internal.pageSize.getHeight();
  const candidates = ["/public/letterheads/saam-letterhead.png", "/letterheads/saam-letterhead.png"];
  for (const path of candidates) {
    try {
      const dataURL = await fetchToDataURL(path);
      if (dataURL) { pdf.addImage(dataURL, /\.png/i.test(path)?"PNG":"JPEG", 0, 0, W, H); return; }
      const img = new Image(); img.crossOrigin = "anonymous";
      await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = path; });
      pdf.addImage(img, 0, 0, W, H); return;
    } catch { /* next */ }
  }
}
async function downloadProjectPDF(project) {
  const { default: jsPDF } = await import("jspdf");
  const pdf = new jsPDF({ unit: "pt", format: "a4" });

  // Layout tokens
  const W = pdf.internal.pageSize.getWidth();
  const H = pdf.internal.pageSize.getHeight();
  const LM = 64, RM = 64;              // side margins
  const GRID = { gap: 12, row: 18 };   // rhythm
  const COLOR = {
    fg: "#0f172a", sub: "#475569", rule: "#e5e7eb",
    blue: [37,99,235], mutedLine: "#eef2f7",
    green: [16,185,129], amber: [245,158,11], red: [244,63,94], slate: [148,163,184]
  };

  let y = 154; // start a little lower to sit under letterhead
  await renderLetterhead(pdf);

  // ---------- Title ----------
  pdf.setFont("Helvetica", "bold"); pdf.setFontSize(22); pdf.setTextColor(COLOR.fg);
  pdf.text("PROJECT SUMMARY", LM, y);
  drawRule(pdf, LM, y + 10, W - RM, COLOR.rule);
  y += GRID.row + 14;

  // ---------- Capsule Header ----------
  const capX = LM, capW = W - LM - RM, capH = 96;
  pdf.setFillColor(248,250,252); pdf.roundedRect(capX, y, capW, capH, 12, 12, "F");
  pdf.setDrawColor("#dbe0e6"); pdf.roundedRect(capX, y, capW, capH, 12, 12, "S");
  pdf.setFillColor(...COLOR.blue); pdf.roundedRect(capX, y, 6, capH, 12, 0, "F");

  const title = (project.pacsName || project.name || "PROJECT").toUpperCase();
  const pid = project.projectId || project.id || "—";
  const district = project.district || "—";
  const owner = project.owner || "—";
  const status = project.currentStage || "—";
  const progress = typeof project.progressPercent === "number" ? `${project.progressPercent}%` : "—";
  const created = project.createdAt ? formatDate(project.createdAt) : "—";
  const updated = project.lastUpdated ? formatDate(project.lastUpdated) : "—";

  pdf.setFont("Helvetica", "bold"); pdf.setFontSize(14); pdf.setTextColor(COLOR.fg);
  pdf.text(title, capX + 18, y + 26);
  pdf.text(pid, capX + capW - 18, y + 26, { align: "right" });

  pdf.setFont("Helvetica", "normal"); pdf.setFontSize(11); pdf.setTextColor(COLOR.sub);
  pdf.text(`District: ${district}   •   Owner: ${owner}`, capX + 18, y + 46);
  pdf.text(`Status: ${status}   •   Progress: ${progress}`, capX + 18, y + 64);
  pdf.text(`Created: ${created}`, capX + capW - 18, y + 46, { align: "right" });
  pdf.text(`Updated: ${updated}`, capX + capW - 18, y + 64, { align: "right" });

  y += capH + GRID.row;

  // ---------- Progress ----------
  y = drawProgressBarPro(pdf, LM, y, W - LM - RM, Number(project.progressPercent || 0), COLOR);

  // ---------- Key Details (2 columns table, auto-wrap) ----------
  y += GRID.gap;
  const kdX = LM, kdW = W - LM - RM;
  const rows = [
    ["Project", title], ["Project ID", pid],
    ["District", district], ["Owner", owner],
  ];
  y = drawKeyDetails(pdf, kdX, y, kdW, rows, COLOR);

  // ---------- Milestones (2-column, aligned) ----------
  y += GRID.row;
  const stages = Array.isArray(project.stages) ? project.stages : [];
  if (stages.length) {
    y = drawMilestones(pdf, LM, y, W - LM - RM, stages, COLOR, H, addPageFactory(pdf, () => [LM, 120]));
  }

  pdf.save(`Project_${(project.pacsName || project.name || "summary").replace(/[^a-z0-9]/gi, "_")}.pdf`);
}

// draw thin divider
function drawRule(pdf, x1, y, x2, color) {
  pdf.setDrawColor(color); pdf.line(x1, y, x2, y);
}

// big, premium progress bar with inline percent
function drawProgressBarPro(pdf, x, y, width, percent, COLOR) {
  const pct = Math.max(0, Math.min(100, Math.round(percent)));
  pdf.setFont("Helvetica", "bold"); pdf.setFontSize(12); pdf.setTextColor(COLOR.fg);
  pdf.text("Overall Progress", x, y);
  const barY = y + 10;

  // trail
  pdf.setDrawColor("#e2e8f0"); pdf.setFillColor(255,255,255);
  pdf.roundedRect(x, barY, width, 14, 7, 7, "S");

  // fill
  pdf.setFillColor(...COLOR.blue);
  const fillW = Math.max(0, Math.min(width, (width * pct) / 100));
  pdf.roundedRect(x, barY, fillW, 14, 7, 7, "F");

  // inline percent
  pdf.setFont("Helvetica", "normal"); pdf.setFontSize(11); pdf.setTextColor(COLOR.sub);
  pdf.text(`${pct}%`, x + width - 26, barY + 11);
  return barY + 28;
}

// two-column “Key Details” with wrapping and neat grid
function drawKeyDetails(pdf, x, y, w, pairs, COLOR) {
  const cardH = 130, colW = w / 2 - 24;
  const startY = y;
  pdf.setFillColor(255,255,255); pdf.roundedRect(x, y, w, cardH, 10, 10, "F");
  pdf.setDrawColor(COLOR.rule); pdf.roundedRect(x, y, w, cardH, 10, 10, "S");

  pdf.setFont("Helvetica","bold"); pdf.setFontSize(13); pdf.setTextColor(COLOR.fg);
  pdf.text("KEY DETAILS", x + 14, y + 22);
  drawRule(pdf, x + 14, y + 28, x + w - 14, COLOR.rule);

  const rowStart = y + 48;
  // left column
  renderKV(pdf, x + 20, rowStart, [["Project", pairs[0][1]], ["District", pairs[2][1]]], colW, COLOR);
  // right column
  renderKV(pdf, x + 20 + w/2, rowStart, [["Project ID", pairs[1][1]], ["Owner", pairs[3][1]]], colW, COLOR);

  return startY + cardH + 6;
}
function renderKV(pdf, x, y, kvRows, maxW, COLOR) {
  pdf.setFontSize(11);
  kvRows.forEach(([k,v], idx) => {
    pdf.setFont("Helvetica","bold"); pdf.setTextColor(COLOR.fg);
    pdf.text(`${k}:`, x, y);
    pdf.setFont("Helvetica","normal");
    const text = pdf.splitTextToSize(String(v ?? "—"), maxW - 80);
    const topLine = y;
    text.forEach((ln,i) => { pdf.text(ln, x + 80, y); y += 14; });
    drawRule(pdf, x, topLine + 10, x + maxW, COLOR.mutedLine);
    y += (idx === kvRows.length - 1 ? 0 : 4);
  });
}

// Milestones: two columns, status dots + right aligned status/date
function drawMilestones(pdf, x, y, w, stages, COLOR, H, ensurePage) {
  const preCodes = ["MOU","SURVEY","SOIL","DESIGN","TENDER","WORK"];
  const pre = stages.filter(s=>preCodes.includes(s.code));
  const post = stages.filter(s=>!preCodes.includes(s.code));

  const startY = y;
  pdf.setFillColor(255,255,255); pdf.roundedRect(x, y, w, 200, 10, 10, "F"); // height will be adjusted visually by content
  pdf.setDrawColor(COLOR.rule); pdf.roundedRect(x, y, w, 200, 10, 10, "S");

  pdf.setFont("Helvetica","bold"); pdf.setFontSize(13); pdf.setTextColor(COLOR.fg);
  pdf.text("MILESTONES", x + 14, y + 22);
  drawRule(pdf, x + 14, y + 28, x + w - 14, COLOR.rule);

  const colW = (w - 40) / 2;
  const L = x + 20, R = L + colW + 20;
  let ly = y + 46, ry = y + 46;

  // column headers
  pdf.setFont("Helvetica","bold"); pdf.setFontSize(11);
  pdf.text("Pre-Work", L, ly); pdf.text("Post-Work", R, ry);
  ly += 18; ry += 18;

  // rows
  const renderCol = (colX, colW, list) => {
    let yy = colX === L ? ly : ry;
    pdf.setFont("Helvetica","normal"); pdf.setFontSize(11); pdf.setTextColor(COLOR.fg);
    const statusX = colX + colW - 120; // right area for status/date
    list.forEach(s => {
      yy = ensurePage(yy); // auto page break if needed
      // status color
      const st = String(s.status || "Pending").toLowerCase();
      const dot = st==="completed" ? COLOR.green : st==="in progress" ? COLOR.amber : st==="rejected" ? COLOR.red : COLOR.slate;

      pdf.setFillColor(...dot);
      pdf.circle(colX + 4, yy - 4, 2.2, "F");

      // name (trim)
      const name = String(s.name || s.code || "");
      const nameLines = pdf.splitTextToSize(name, (statusX - 16) - (colX + 12));
      nameLines.forEach((ln, i) => { pdf.text(ln, colX + 12, yy + (i*14)); });

      // status/date right aligned
      const when =
        s.completedAt?.toDate?.() ? s.completedAt.toDate() :
        s.startedAt?.toDate?.() ? s.startedAt.toDate() :
        s.lastStageUpdatedAt?.toDate?.() ? s.lastStageUpdatedAt.toDate() : null;
      const whenStr = when ? formatDateOnly(when) : "—";
      const stLabel = st==="in progress" ? "In Progress" : st.charAt(0).toUpperCase() + st.slice(1);
      pdf.setTextColor(COLOR.sub);
      pdf.text(`• ${stLabel} — ${whenStr}`, colX + colW - 10, yy, { align: "right" });
      pdf.setTextColor(COLOR.fg);

      yy += Math.max(18, nameLines.length * 14 + 2);
    });
    return yy;
  };

  ly = renderCol(L, colW, pre.length ? pre : [{ name:"—", status:"pending" }]);
  ry = renderCol(R, colW, post.length ? post : [{ name:"—", status:"pending" }]);

  // adjust visual bottom border (optional)
  const endY = Math.max(ly, ry) + 10;
  return endY;
}

// create an ensurePage() helper for long tables
function addPageFactory(pdf, getStart) {
  const H = pdf.internal.pageSize.getHeight();
  return (yy) => {
    if (yy > H - 80) {
      pdf.addPage();
      const [LM, top] = getStart();
      return top;
    }
    return yy;
  };
}

/* ============================ Applications → Project mapping ============================ */
function mapAppToProjectFields(app) {
  return {
    pacsName: app.societyName || app.applicantSociety || app.name || "",
    name: app.societyName || app.applicantSociety || app.name || "",
    district: app.district || (app.location && app.location.district) || "",
    owner: app.applicantName || app.contactPerson || "",
    notes: [
      app.email,
      app.state,
      app.village || (app.address && app.address.village),
      app.taluka || (app.address && app.address.taluka),
      app.pincode || (app.address && app.address.pincode),
      app.capacityMt ? `${app.capacityMt} MT` : "",
    ].filter(Boolean).join(" • "),
  };
}

/* ============================ Main Page ============================ */
export default function ProjectsDashboard() {
  // list (left)
  const [projects, setProjects] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [qText, setQText] = useState("");
  const [district, setDistrict] = useState("");
  const [sortKey, setSortKey] = useState("lastUpdated:desc"); // SINGLE declaration

  // detail (right)
  const [selId, setSelId] = useState(null);
  const [proj, setProj] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState("");

  // stage editor modal
  const [edit, setEdit] = useState({ open: false, code: "", status: "Pending", dateISO: nowLocalISO(), note: "" });

  // create modal (tabs)
  const [createOpen, setCreateOpen] = useState(false);
  const [createSaving, setCreateSaving] = useState(false);
  const [createTab, setCreateTab] = useState("app"); // 'app' | 'manual'
  const [createForm, setCreateForm] = useState({ name: "", pacsName: "", district: "", owner: "", startDate: "", notes: "" });
  const setCF = (k, v) => setCreateForm(f => ({ ...f, [k]: v }));

  // approved applications stream (from 'applications' collection)
  const [apps, setApps] = useState([]);
  const [appsLoading, setAppsLoading] = useState(true);
  const [appsError, setAppsError] = useState("");
  const [appSearch, setAppSearch] = useState("");
  const [selectedAppId, setSelectedAppId] = useState(null);

  // deletion flag
  const [deletingId, setDeletingId] = useState(null);

  /* ---- Stream projects ---- */
  useEffect(() => {
    const qRef = query(collection(db, "projects"), orderBy("lastUpdated", "desc"));
    const unsub = onSnapshot(qRef, (snap) => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setProjects(list);
      setLoadingList(false);
      if (!selId && list.length) setSelId(list[0].id);
    }, (err) => { console.error("[list] error", err); setLoadingList(false); });
    return () => unsub();
  }, [selId]);

  /* ---- Stream Approved Applications (no index required) ---- */
  useEffect(() => {
    setAppsError(""); setAppsLoading(true);
    const root = collection(db, "applications");
    const q1 = query(root, where("status", "==", "approved"));
    const unsub = onSnapshot(q1, (snap) => {
      let arr = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      arr.sort((a, b) => {
        const aa = a.submittedAt?.toDate ? a.submittedAt.toDate().getTime()
          : a.createdAt?.toDate ? a.createdAt.toDate().getTime()
          : 0;
        const bb = b.submittedAt?.toDate ? b.submittedAt.toDate().getTime()
          : b.createdAt?.toDate ? b.createdAt.toDate().getTime()
          : 0;
        return bb - aa;
      });
      setApps(arr);
      setAppsLoading(false);
    }, (e) => {
      setAppsError(e?.message || String(e));
      setAppsLoading(false);
    });

    return () => unsub();
  }, []);

  /* ---- Load selected project ---- */
  useEffect(() => {
    if (!selId) { setProj(null); return; }
    (async () => {
      setLoadingDetail(true); setError("");
      try {
        const ref = doc(db, "projects", selId);
        const snap = await getDoc(ref);
        if (!snap.exists()) { setProj(null); setError("Project not found."); setLoadingDetail(false); return; }
        const data = snap.data(); const stages = mergeStages(Array.isArray(data.stages) ? data.stages : []);
        setProj({ id: snap.id, ...data, stages });
      } catch (e) { console.error("[detail] error", e); setError("Failed to load project."); }
      finally { setLoadingDetail(false); }
    })();
  }, [selId]);

  // filters & sort
  const filtered = useMemo(() => {
    const t = qText.trim().toLowerCase();
    const [key, dir = "asc"] = sortKey.split(":");
    let rows = projects.filter(p => {
      if (district && p.district !== district) return false;
      if (!t) return true;
      const hay = `${p.pacsName || ""} ${p.name || ""} ${p.district || ""} ${p.currentStage || ""}`.toLowerCase();
      return hay.includes(t);
    });
    rows.sort((a, b) => {
      const m = dir === "asc" ? 1 : -1;
      if (key === "lastUpdated") {
        const aa = a.lastUpdated?.toDate ? a.lastUpdated.toDate().getTime() : new Date(a.lastUpdated || 0).getTime();
        const bb = b.lastUpdated?.toDate ? b.lastUpdated.toDate().getTime() : new Date(b.lastUpdated || 0).getTime();
        return (aa - bb) * m;
      }
      if (key === "progress") return ((a.progressPercent || 0) - (b.progressPercent || 0)) * m;
      return String(a.pacsName || a.name || "").localeCompare(String(b.pacsName || b.name || "")) * m;
    });
    return rows;
  }, [projects, qText, district, sortKey]);

  const progress = useMemo(() => computeProgress(proj?.stages || []), [proj]);

  /* ---- Stage update ---- */
  async function applyStageUpdate(code, status, opts = {}) {
    try {
      const whenISO = opts.dateISO || ""; const note = opts.note || "";
      let ts = null; if (whenISO) { const d = new Date(whenISO); if (!isNaN(d.getTime())) ts = Timestamp.fromDate(d); }
      const now = Timestamp.now();

      const stages = (proj?.stages || []).map(s => {
        if (s.code !== code) return s;
        const norm = String(status || "").toLowerCase();
        const next = { ...s, status: norm === "in progress" ? "In Progress" : norm.charAt(0).toUpperCase() + norm.slice(1), lastStageUpdatedAt: now };
        if (note) next.lastStageNote = note;
        if (norm === "in progress") { next.startedAt = ts || s.startedAt || now; next.completedAt = null; next.rejectedAt = null; }
        else if (norm === "completed") { next.completedAt = ts || now; }
        else if (norm === "rejected") { next.rejectedAt = ts || now; }
        else if (norm === "pending") { next.startedAt = null; next.completedAt = null; next.rejectedAt = null; }
        return next;
      });

      const ref = doc(db, "projects", proj.id);
      const progressPercent = computeProgress(stages);
      const currentStage = computeCurrentStage(stages);

      await updateDoc(ref, {
        stages,
        progressPercent,
        currentStage,
        lastUpdated: serverTimestamp(),
        projectDashboardSyncedAt: serverTimestamp(),
      });

      await syncProjectStageToWorkflowAndStakeholder(proj, stages, code);

      setProj(p => ({ ...p, stages, progressPercent, currentStage }));
    } catch (e) { console.error("[applyStageUpdate] error", e); alert(`Update failed: ${e?.message || e}`); }
  }

  /* ---- Contractor save ---- */
  async function saveContractor(payload) {
    try {
      const clean = { ...payload };
      if (payload.loaDate instanceof Date) clean.loaDate = Timestamp.fromDate(payload.loaDate);
      await updateDoc(doc(db, "projects", proj.id), {
        ...clean,
        lastUpdated: serverTimestamp(),
        projectDashboardContractorSyncedAt: serverTimestamp(),
      });

      await syncContractorToStakeholder(proj, clean);

      setProj(p => ({ ...p, ...clean }));
    } catch (e) { console.error("[saveContractor] error", e); alert(`Failed to save contractor: ${e?.message || e}`); }
  }

  /* ---- CSV export ---- */
  function exportListCSV() {
    const rows = filtered.map(p => ({
      Project: p.pacsName || p.name || "",
      District: p.district || "",
      Progress: clamp(p.progressPercent || 0),
      CurrentStage: p.currentStage || "",
      LastUpdated: formatDate(p.lastUpdated),
      ProjectID: p.projectId || p.id,
    }));
    downloadCSV(`projects_${new Date().toISOString().slice(0,10)}.csv`, rows);
  }

  /* ---- Bulk actions (Pre-Work) ---- */
  async function bulkPreworkComplete() {
    if (!proj) return; if (!confirm("Mark all Pre-Work stages as Completed with current time?")) return;
    try {
      const now = Timestamp.now();
      const stages = (proj.stages || []).map(s => PRE_WORK.some(p => p.code === s.code)
        ? { ...s, status: "Completed", completedAt: now, lastStageUpdatedAt: now } : s);
      const ref = doc(db, "projects", proj.id);
      const progressPercent = computeProgress(stages); const currentStage = computeCurrentStage(stages);
      await updateDoc(ref, {
        stages,
        progressPercent,
        currentStage,
        lastUpdated: serverTimestamp(),
        projectDashboardSyncedAt: serverTimestamp(),
      });

      await syncBulkStagesToWorkflow(proj, stages);

      setProj(p => ({ ...p, stages, progressPercent, currentStage }));
    } catch (e) { console.error("[bulkPreworkComplete] error", e); alert("Bulk update failed."); }
  }
  async function bulkPreworkReset() {
    if (!proj) return; if (!confirm("Reset all Pre-Work stages to Pending?")) return;
    try {
      const now = Timestamp.now();
      const stages = (proj.stages || []).map(s => PRE_WORK.some(p => p.code === s.code)
        ? { ...s, status: "Pending", startedAt: null, completedAt: null, rejectedAt: null, lastStageUpdatedAt: now } : s);
      const ref = doc(db, "projects", proj.id);
      const progressPercent = computeProgress(stages); const currentStage = computeCurrentStage(stages);
      await updateDoc(ref, {
        stages,
        progressPercent,
        currentStage,
        lastUpdated: serverTimestamp(),
        projectDashboardSyncedAt: serverTimestamp(),
      });

      await syncBulkStagesToWorkflow(proj, stages);

      setProj(p => ({ ...p, stages, progressPercent, currentStage }));
    } catch (e) { console.error("[bulkPreworkReset] error", e); alert("Bulk update failed."); }
  }

  /* ---- Derived: apps search filter ---- */
  const filteredApps = useMemo(() => {
    const t = appSearch.trim().toLowerCase();
    if (!t) return apps;
    return apps.filter(a => {
      const hay = `${a.societyName||""} ${a.applicantName||""} ${a.district||""} ${a.state||""} ${a.village||""} ${a.email||""}`.toLowerCase();
      return hay.includes(t);
    });
  }, [apps, appSearch]);

  function prefillFromApp(app) {
    const base = mapAppToProjectFields(app);
    setCreateForm(f => ({ ...f, ...base, startDate: "" }));
  }

  /* ---- Create (app/manual) ---- */
  async function createProject() {
    try {
      if (createTab === "app") {
        if (!selectedAppId) { alert("Please select an approved application"); return; }
        const app = apps.find(x => x.id === selectedAppId);
        if (!app) { alert("Selected application not found"); return; }
        const dup = projects.some(p => p.sourceApplicationId === app.id);
        if (dup) { alert("Project already exists for this application."); return; }

        const mapped = mapAppToProjectFields(app);
        const payload = {
          ...mapped,
          stages: mergeStages([]),
          progressPercent: 0,
          currentStage: "MOU",
          projectId: `PRJ-${Date.now().toString().slice(-6)}`,
          sourceApplicationId: app.id,
          sourceApplicationStatus: app.status || "approved",
          createdAt: serverTimestamp(),
          lastUpdated: serverTimestamp(),
        };
        setCreateSaving(true);
        await addDoc(collection(db, "projects"), payload);
        setCreateSaving(false);
        setCreateOpen(false);
        setSelectedAppId(null); setAppSearch("");
        return;
      }

      // manual
      if (!createForm.name?.trim() && !createForm.pacsName?.trim()) { alert("Please enter Project / PACS name"); return; }
      setCreateSaving(true);
      const payload = {
        name: createForm.name?.trim() || "",
        pacsName: createForm.pacsName?.trim() || "",
        district: createForm.district || "",
        owner: createForm.owner || "",
        startDate: createForm.startDate || "",
        notes: createForm.notes || "",
        stages: mergeStages([]),
        progressPercent: 0,
        currentStage: "MOU",
        projectId: `PRJ-${Date.now().toString().slice(-6)}`,
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp(),
      };
      await addDoc(collection(db, "projects"), payload);
      setCreateSaving(false); setCreateOpen(false);
      setCreateForm({ name: "", pacsName: "", district: "", owner: "", startDate: "", notes: "" });
    } catch (e) {
      console.error("[createProject] error", e);
      setCreateSaving(false); alert(e?.message || "Create failed");
    }
  }

  /* ---- Delete Project (robust) ---- */
  async function deleteProject(p) {
    if (!p) return;
    const sure = window.confirm(
      `Delete project “${p.pacsName || p.name || p.id}”?`
      + `\nThis will remove the Firestore record`
      + `\n(and try to delete any files under /projects/${p.id} in Storage if present).`
    );
    if (!sure) return;

    try {
      setDeletingId(p.id);
      await deleteDoc(doc(db, "projects", p.id));
      const still = await getDoc(doc(db, "projects", p.id));
      if (still.exists()) {
        alert("Delete did not complete due to Firestore rules/permission. Please check security rules.");
        setDeletingId(null); return;
      }

      setProjects(prev => prev.filter(x => x.id !== p.id));
      if (selId === p.id) { const next = projects.find(x => x.id !== p.id) || null; setSelId(next ? next.id : null); setProj(null); }

      try {
        const root = storageRef(storage, `projects/${p.id}`);
        const stack = [root];
        while (stack.length) {
          const folder = stack.pop();
          // eslint-disable-next-line no-await-in-loop
          const res = await listAll(folder);
          // eslint-disable-next-line no-await-in-loop
          await Promise.all(res.items.map(i => deleteObject(i).catch(()=>null)));
          res.prefixes.forEach(pref => stack.push(pref));
        }
      } catch {}

      alert("Project deleted.");
    } catch (e) {
      console.error("[deleteProject] error", e);
      alert(`Delete failed: ${e?.code || ""} ${e?.message || e}`);
    } finally {
      setDeletingId(null);
    }
  }

  /* ============================== JSX ============================== */
  return (
    <AdminGate>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white/90 backdrop-blur border-b border-slate-200">
          <div className="max-w-[120rem] mx-auto px-4 py-3 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div>
              <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-blue-700 via-teal-600 to-emerald-600">
                Projects
              </h1>
              <p className="text-sm text-slate-600">
                Create projects directly from <b>Approved Applications</b>, track status & documents.
              </p>
              {appsError && (
                <div className="mt-1 text-xs text-rose-600">
                  Applications stream note: {appsError}
                </div>
              )}
            </div>
            {/* ✅ Header buttons: Create Project + Export CSV + Logout */}
            <div className="flex flex-wrap items-center gap-2">
              {/* NEW BUTTON – opens create modal (From Application tab by default) */}
              <button
                onClick={() => { setCreateTab("app"); setCreateOpen(true); }}
                className="px-4 py-2 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 shadow-sm"
              >
                + Create Project
              </button>

              <button
                onClick={() =>
                  downloadCSV(
                    `projects_${new Date().toISOString().slice(0, 10)}.csv`,
                    filtered.map((p) => ({
                      Project: p.pacsName || p.name || "",
                      District: p.district || "",
                      Progress: clamp(p.progressPercent || 0),
                      CurrentStage: p.currentStage || "",
                      LastUpdated: formatDate(p.lastUpdated),
                      ProjectID: p.projectId || p.id,
                    }))
                  )
                }
                className="px-4 py-2 rounded-xl border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 text-sm"
              >
                Export CSV
              </button>

              <button
                onClick={() => {
                  adminLogout();
                  window.location.href = "/admin-login";
                }}
                className="px-4 py-2 rounded-xl bg-red-600 text-white hover:bg-red-700 text-sm"
              >
                Logout
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="max-w-[120rem] mx-auto px-4 py-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <div className="flex flex-col md:flex-row md:items-center gap-3">
              <input value={qText} onChange={(e) => setQText(e.target.value)}
                placeholder="Search project / PACS / district / stage…" className="flex-1 border border-slate-300 rounded-xl px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"/>
              <select value={district} onChange={(e) => setDistrict(e.target.value)} className="border border-slate-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500">
                <option value="">All Districts</option>
                {Array.from(new Set(projects.map(p => p.district).filter(Boolean))).sort().map(d => <option key={d} value={d}>{d}</option>)}
              </select>
              <select value={sortKey} onChange={(e) => setSortKey(e.target.value)} className="border border-slate-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500">
                <option value="lastUpdated:desc">Last Updated ↓</option>
                <option value="lastUpdated:asc">Last Updated ↑</option>
                <option value="progress:desc">Progress ↓</option>
                <option value="progress:asc">Progress ↑</option>
                <option value="name:asc">Name A→Z</option>
                <option value="name:desc">Name Z→A</option>
              </select>
            </div>
          </div>
        </div>

        {/* Master–Detail */}
        <div className="max-w-[120rem] mx-auto px-4 pb-12 grid grid-cols-1 xl:grid-cols-12 gap-5">
          {/* LEFT List */}
          <div className="xl:col-span-5">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-left">
                    <th className="p-3 border-b">Project / PACS</th>
                    <th className="p-3 border-b">District</th>
                    <th className="p-3 border-b">Progress</th>
                    <th className="p-3 border-b">Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingList ? (
                    <tr><td colSpan={4} className="p-6 text-center text-slate-500">Loading…</td></tr>
                  ) : filtered.length === 0 ? (
                    <tr><td colSpan={4} className="p-6 text-center text-slate-500">No projects</td></tr>
                  ) : (
                    filtered.map(p => {
                      const active = selId === p.id;
                      return (
                        <tr key={p.id} onClick={() => setSelId(p.id)}
                          className={clsx("cursor-pointer hover:bg-slate-50", active && "bg-slate-50", p.id===deletingId && "opacity-60")}>
                          <td className="p-3 border-t">
                            <div className="font-semibold text-slate-900">{p.pacsName || p.name || "—"}</div>
                            <div className="text-xs text-slate-500">{p.projectId || p.id}</div>
                          </td>
                          <td className="p-3 border-t">{p.district || "—"}</td>
                          <td className="p-3 border-t w-48">
                            <div className="flex items-center gap-3">
                              <ProgressBar value={p.progressPercent || 0}/>
                              <div className="text-xs w-8 text-right">{clamp(p.progressPercent || 0)}%</div>
                            </div>
                          </td>
                          <td className="p-3 border-t text-xs text-slate-600">{formatDate(p.lastUpdated)}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* RIGHT Detail */}
          <div className="xl:col-span-7">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
              {!selId ? (
                <div className="p-10 text-center text-slate-500">Select a project from the left.</div>
              ) : loadingDetail ? (
                <div className="p-10 text-center text-slate-500">Loading project…</div>
              ) : error ? (
                <div className="rounded-xl border border-rose-200 bg-rose-50 text-rose-700 px-4 py-3">{error}</div>
              ) : !proj ? (
                <div className="p-10 text-center text-slate-500">Project not found.</div>
              ) : (
                <>
                  {/* Header */}
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                    <div>
                      <h2 className="text-xl font-extrabold text-slate-900">{proj.pacsName || proj.name} <span className="text-slate-500 font-normal">(PACS)</span></h2>
                      <div className="text-xs text-slate-600 mt-1">ID: {proj.projectId || proj.id} • {proj.district || "—"}</div>
                      <div className="mt-2">
                        <div className="flex items-center justify-between text-xs text-slate-600 mb-1">
                          <div className="inline-flex items-center gap-2">
                            <StatusBadge s={progress >= 100 ? "Completed" : "In Progress"} />
                            <span className="text-slate-500">Last updated: {formatDate(proj.lastUpdated)}</span>
                          </div>
                          <div className="text-slate-600">{clamp(progress)}% complete</div>
                        </div>
                        <ProgressBar value={progress}/>
                      </div>
                    </div>
                    <div className="shrink-0 flex items-center gap-2">
                      <button
                        onClick={() => downloadProjectPDF(proj)}
                        className="px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 shadow"
                        disabled={deletingId === proj.id}
                      >
                        ⬇ Download PDF
                      </button>
                      <button
                        onClick={() => deleteProject(proj)}
                        className={clsx(
                          "px-4 py-2 rounded-xl text-white shadow",
                          deletingId === proj.id ? "bg-rose-400 cursor-not-allowed" : "bg-rose-600 hover:bg-rose-700"
                        )}
                        disabled={deletingId === proj.id}
                        title="Delete this project"
                      >
                        {deletingId === proj.id ? "Deleting…" : "Delete"}
                      </button>
                    </div>
                  </div>

                  {/* Pre-Work */}
                  <SectionHeading title="Pre-Work Timeline" subtitle="(Till Work Started)"/>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    {proj.stages.filter(s => PRE_WORK.some(p => p.code === s.code)).map(s => (
                      <StageCard key={s.code} s={s}
                        onEdit={() => setEdit({ open: true, code: s.code, status: s.status || "Pending", dateISO: nowLocalISO(), note: s.lastStageNote || "" })}/>
                    ))}
                  </div>

                  {/* Contractor */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">Assigned Contractor</h3>
                    {proj.contractorName ? (
                      <div className="bg-white rounded-xl border border-emerald-200 shadow-sm p-4">
                        <div className="text-sm text-slate-800">
                          <span className="font-medium">{proj.contractorName}</span>
                          {proj.contractorCompany ? ` • ${proj.contractorCompany}` : ""}
                        </div>
                        <div className="text-xs text-slate-600 mt-1">
                          {proj.contractorMobile ? `Contact: ${proj.contractorMobile} • ` : ""}{proj.loaDate ? `LOA: ${formatDate(proj.loaDate)}` : ""}
                        </div>
                      </div>
                    ) : (
                      <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-3 rounded-xl">Contractor not assigned yet.</div>
                    )}
                    <div className="mt-3 bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                      <div className="grid md:grid-cols-4 gap-3">
                        <Input label="Contractor Name" value={proj.contractorName || ""} onChange={(v) => setProj(p => ({ ...p, contractorName: v }))}/>
                        <Input label="Company" value={proj.contractorCompany || ""} onChange={(v) => setProj(p => ({ ...p, contractorCompany: v }))}/>
                        <Input label="Mobile" value={proj.contractorMobile || ""} onChange={(v) => setProj(p => ({ ...p, contractorMobile: v }))}/>
                        <Input type="datetime-local" label="LOA Date"
                          value={proj.loaDate ? (proj.loaDate?.toDate?.() ? proj.loaDate.toDate().toISOString().slice(0, 16) : nowLocalISO()) : ""}
                          onChange={(v) => { const d = v ? new Date(v) : null; setProj(p => ({ ...p, loaDate: d && !isNaN(d.getTime()) ? d : null })); }}/>
                      </div>
                      <div className="mt-3 text-right">
                        <button onClick={() => saveContractor({
                          contractorName: proj.contractorName || "",
                          contractorCompany: proj.contractorCompany || "",
                          contractorMobile: proj.contractorMobile || "",
                          loaDate: proj.loaDate || null,
                        })} className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm hover:bg-emerald-700">Save Contractor</button>
                      </div>
                    </div>
                  </div>

                  {/* Post-Work */}
                  <SectionHeading title="Post-Work Timeline" subtitle="(Construction milestones)"/>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {proj.stages.filter(s => POST_WORK.some(p => p.code === s.code)).map(s => (
                      <StageCard key={s.code} s={s}
                        onEdit={() => setEdit({ open: true, code: s.code, status: s.status || "Pending", dateISO: nowLocalISO(), note: s.lastStageNote || "" })}/>
                    ))}
                  </div>

                  {/* Bulk actions */}
                  <div className="mt-6 flex flex-wrap gap-2">
                    <button onClick={bulkPreworkComplete} className="px-3 py-1.5 rounded-lg bg-emerald-100 text-emerald-700 text-xs hover:bg-emerald-200">Mark all Pre-Work Completed</button>
                    <button onClick={bulkPreworkReset} className="px-3 py-1.5 rounded-lg bg-slate-100 text-slate-700 text-xs hover:bg-rose-100">Reset all Pre-Work</button>
                  </div>

                  {/* Stage Editor Modal */}
                  {edit.open && (
                    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4">
                      <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-2xl relative">
                        <button className="absolute top-4 right-4 text-2xl text-slate-500 hover:text-slate-700" onClick={() => setEdit(e => ({ ...e, open: false }))}>×</button>
                        <h4 className="text-lg font-semibold text-slate-900 mb-4">Update Stage — {NAME_BY[edit.code]}</h4>
                        <div className="grid gap-3">
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">Status</label>
                            <select className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              value={edit.status} onChange={(e) => setEdit(x => ({ ...x, status: e.target.value }))}>
                              <option>Pending</option><option>In Progress</option><option>Completed</option><option>Rejected</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">Date & Time</label>
                            <input type="datetime-local" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              value={edit.dateISO} onChange={(e) => setEdit(x => ({ ...x, dateISO: e.target.value }))}/>
                            <button onClick={() => setEdit(x => ({ ...x, dateISO: nowLocalISO() }))} className="mt-1 text-xs text-blue-600 hover:underline">Use current time</button>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">Remark (optional)</label>
                            <textarea rows={3} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              value={edit.note} onChange={(e) => setEdit(x => ({ ...x, note: e.target.value }))} placeholder="Note about this update…"/>
                          </div>
                        </div>
                        <div className="mt-4 text-right">
                          <button onClick={() => setEdit(e => ({ ...e, open: false }))} className="px-4 py-2 rounded-lg bg-slate-200 text-slate-800 text-sm hover:bg-slate-300 mr-2">Cancel</button>
                          <button onClick={async () => { await applyStageUpdate(edit.code, edit.status, { dateISO: edit.dateISO, note: edit.note }); setEdit(e => ({ ...e, open: false })); }} className="px-4 py-2 rounded-lg bg-gradient-to-r from-teal-600 to-cyan-600 text-white text-sm hover:shadow">Save</button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Add Project Modal (Tabs: From Application / Manual) */}
        {createOpen && (
          <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-3xl p-6 shadow-2xl relative">
              <button className="absolute top-4 right-4 text-2xl text-slate-500 hover:text-slate-700" onClick={() => setCreateOpen(false)}>×</button>

              {/* Tabs */}
              <div className="flex items-center gap-2 mb-4">
                <button
                  className={clsx("px-3 py-1.5 rounded-lg text-sm",
                    createTab==="app" ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-700")}
                  onClick={()=>setCreateTab("app")}
                >From Approved Application</button>
                <button
                  className={clsx("px-3 py-1.5 rounded-lg text-sm",
                    createTab==="manual" ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-700")}
                  onClick={()=>setCreateTab("manual")}
                >Manual</button>
              </div>

              {createTab==="app" ? (
                <>
                  <div className="mb-3 text-sm text-slate-600">
                    Select an <b>Approved</b> application to pre-fill the project. Newly approved appear automatically.
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
                    <div className="md:col-span-1">
                      <input
                        value={appSearch}
                        onChange={e=>setAppSearch(e.target.value)}
                        placeholder="Search society/applicant/village/email…"
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                      />
                      <div className="mt-2 max-h-64 overflow-auto border rounded-lg">
                        {appsLoading ? (
                          <div className="p-3 text-xs text-slate-500">Loading applications…</div>
                        ) : filteredApps.length===0 ? (
                          <div className="p-3 text-xs text-slate-500">
                            No approved applications. {appsError && <span className="text-rose-600">({appsError})</span>}
                          </div>
                        ) : (
                          filteredApps.map(a => (
                            <div key={a.id}
                              onClick={()=>{ setSelectedAppId(a.id); setCreateTab("manual"); prefillFromApp(a); }}
                              className={clsx("p-3 border-b cursor-pointer hover:bg-slate-50",
                                selectedAppId===a.id && "bg-blue-50")}
                            >
                              <div className="text-sm font-semibold text-slate-900">{a.societyName || "—"}</div>
                              <div className="text-[11px] text-slate-600">
                                {(a.state||"—")}/{(a.district||"—")} • {a.village||""} {a.taluka?`(${a.taluka})`:""}
                              </div>
                              <div className="text-[11px] text-slate-500">By: {a.applicantName || "—"} • {a.email || "—"}</div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Preview (editable) */}
                    <div className="md:col-span-2">
                      <div className="text-sm font-semibold text-slate-900 mb-2">Project Preview (editable)</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input label="Project Name" value={createForm.name} onChange={v => setCF("name", v)} placeholder="Auto-filled from application"/>
                        <Input label="PACS Name" value={createForm.pacsName} onChange={v => setCF("pacsName", v)} placeholder="Auto-filled from application"/>
                        <Input label="District" value={createForm.district} onChange={v => setCF("district", v)} placeholder="Auto-filled from application"/>
                        <Input label="Owner / In-charge" value={createForm.owner} onChange={v => setCF("owner", v)} placeholder="Applicant name"/>
                        <Input label="Start Date" type="date" value={createForm.startDate} onChange={v => setCF("startDate", v)}/>
                        <Input label="Notes" value={createForm.notes} onChange={v => setCF("notes", v)} placeholder="email • village • taluka • pincode • capacity"/>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 text-right">
                    <button className="px-4 py-2 rounded-lg bg-slate-200 text-slate-800 hover:bg-slate-300 mr-2" onClick={()=>setCreateOpen(false)}>Cancel</button>
                    <button className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
                      disabled={createSaving} onClick={createProject}>
                      {createSaving ? "Creating…" : "Create Project"}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input label="Project Name" value={createForm.name} onChange={v => setCF("name", v)} placeholder="e.g., Warehouse @ XYZ"/>
                    <Input label="PACS Name" value={createForm.pacsName} onChange={v => setCF("pacsName", v)} placeholder="Dabhoda Seva Sahakari Mandali Ltd"/>
                    <Input label="District" value={createForm.district} onChange={v => setCF("district", v)} placeholder="Gandhinagar"/>
                    <Input label="Owner / In-charge" value={createForm.owner} onChange={v => setCF("owner", v)} placeholder="Nareshbhai Chaudhary"/>
                    <Input label="Start Date" type="date" value={createForm.startDate} onChange={v => setCF("startDate", v)}/>
                    <Input label="Notes" value={createForm.notes} onChange={v => setCF("notes", v)} placeholder="Any remarks…"/>
                  </div>
                  <div className="mt-6 text-right">
                    <button className="px-4 py-2 rounded-lg bg-slate-200 text-slate-800 hover:bg-slate-300 mr-2" onClick={()=>setCreateOpen(false)}>Cancel</button>
                    <button className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
                      disabled={createSaving} onClick={createProject}>
                      {createSaving ? "Creating…" : "Create Project"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </AdminGate>
  );
}